import React, { useMemo, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ApolloProvider, ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { IntlProvider, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import Dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import relativeTime from 'dayjs/plugin/relativeTime';
import minMax from 'dayjs/plugin/minMax';
import './custom-validators';
import { AuthContext, useAuthReducer } from './auth';
import { NotificationsProvider } from './notifications';
import Notifications from './components/notifications';
import { createApolloClient, setDefaultClient } from './api/client';
import { AuthCore } from './auth/authCore';
import * as tokenModule from './auth/token';
import './styles/app.scss';
import { WebappRouter } from './routing';
import { PdfExportProvider } from './contexts/PdfExportContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { ModalsProvider } from './contexts/ModalsContext';
import { DealProvider } from './contexts/DealContext';
import { PermissionsProvider } from './contexts/PermissionsContext';

// Use the standard HTML5Backend directly
export const createHTMLBackend = HTML5Backend;

Dayjs.extend(localizedFormat);
Dayjs.extend(relativeTime);
Dayjs.extend(minMax);

const tagManagerArgs = {
  gtmId: import.meta.env.VITE_GTM_ID || '',
  auth: import.meta.env.VITE_GTM_AUTH_KEY,
  preview: import.meta.env.VITE_GTM_PREVIEW,
};

if (import.meta.env.VITE_ENVIRONMENT !== 'development' && import.meta.env.VITE_ENVIRONMENT !== 'test') {
  TagManager.initialize(tagManagerArgs);
}

// AppContent - Inner component that has access to Apollo Client via context
const AppContent = ({ authCore }: { authCore: AuthCore }) => {
  const isPrivate = true;
  const auth = useAuthReducer({ currentPageIsPrivate: isPrivate });

  // Sync auth state token changes to AuthCore
  React.useEffect(() => {
    if (auth.state.token) {
      authCore.setToken(auth.state.token);
    } else {
      authCore.setToken(null);
    }
  }, [auth.state.token, authCore]);

  // Subscribe to AuthCore token changes for logout sync
  // This handles the case where refresh token fails and AuthCore clears the token
  React.useEffect(() => {
    const unsubscribe = authCore.onChange(() => {
      const authCoreToken = authCore.getToken();
      const authStateToken = auth.state.token;

      // If AuthCore cleared token (refresh failed) but auth state still has it, sync logout
      if (!authCoreToken && authStateToken) {
        auth.handlers.logout({ skipApi: true });
      }
    });

    return unsubscribe;
  }, [authCore, auth]);

  return (
    <AuthContext.Provider value={auth}>
      <NavigationProvider>
        <ModalsProvider>
          <DealProvider>
            <PermissionsProvider>
              <NotificationsProvider>
                <DndProvider backend={createHTMLBackend}>
                  <Meta />
                  <Notifications />
                  <PdfExportProvider>
                    <WebappRouter />
                  </PdfExportProvider>
                </DndProvider>
              </NotificationsProvider>
            </PermissionsProvider>
          </DealProvider>
        </ModalsProvider>
      </NavigationProvider>
    </AuthContext.Provider>
  );
};

// App - Outer component that creates Apollo Client without needing auth
const App = () => {
  // change this when more language added in the future
  const locale = 'en';

  // Create a ref to store the Apollo Client for cache clearing in logout
  const clientRef = useRef<ApolloClient<NormalizedCacheObject> | null>(null);

  // Create AuthCore instance with refresh and logout callbacks
  // Note: These callbacks will be called by Apollo error link, not by React auth hooks
  const authCore = useMemo(() => {
    const currentToken = tokenModule.get();

    const core = new AuthCore(
      // Refresh callback - calls the auth API directly
      async () => {
        try {
          // Import auth module dynamically to avoid circular dependency
          const authModule = await import('./api/auth');
          const result = await authModule.refreshToken();
          if (result && typeof result === 'object' && 'success' in result && result.success && 'token' in result && result.token) {
            // Update localStorage token
            tokenModule.set(result.token);
            return { ok: true, accessToken: result.token };
          }
          return { ok: false };
        } catch (error) {
          console.error('Token refresh failed:', error);
          return { ok: false };
        }
      },
      // Logout callback - clears Apollo cache and localStorage token
      async () => {
        tokenModule.remove();
        await clientRef.current?.clearStore();
        // Note: Full logout (navigation, state clear) handled by auth/index.ts
      }
    );

    // Initialize AuthCore with token from localStorage (if exists)
    if (currentToken) {
      core.setToken(currentToken);
    }

    return core;
  }, []);

  // Create Apollo client from factory with authCore
  const client = useMemo(
    () => {
      const newClient = createApolloClient(authCore);
      clientRef.current = newClient;
      // Set default client for backward compatibility
      setDefaultClient(newClient);
      return newClient;
    },
    [authCore]
  );

  Dayjs.locale(locale);

  return (
    <HelmetProvider>
      <IntlProvider locale={locale} textComponent={React.Fragment}>
        <ApolloProvider client={client}>
          <AppContent authCore={authCore} />
        </ApolloProvider>
      </IntlProvider>
    </HelmetProvider>
  );
};

export default App;

export { Dayjs as dayjs };

const Meta = () => {
  const { formatMessage } = useIntl();
  return <Helmet titleTemplate={formatMessage({ id: 'title.template', defaultMessage: '%s | Propdocs' })} />;
};
